<template>
  <v-card class="ui-module-card ui-module-fade-in-up" color="primary">
    <v-card-title>
      <div class="text-h5 font-weight-bold">Resultados C3</div>
    </v-card-title>
    <v-card-text>
      <v-container v-if="loading" fluid>
        <v-row align="center" justify="center" style="min-height: 200px">
          <v-col cols="12" align="center">
            <v-progress-circular indeterminate color="secondary" size="64"></v-progress-circular>
            <div class="text-h6 mt-4">Cargando resultados C3...</div>
          </v-col>
        </v-row>
      </v-container>
      <template v-else>
        <v-data-table
          :headers="headersC3"
          :items="c3Items"
          class="transparent ui-module-fade-in-up"
          hide-default-footer
          disable-pagination
        >
          <template v-slot:[`item.resultado`]="{ item }">
            <span>{{ item.resultado }}</span>
          </template>
          <template v-slot:[`item.fechaVigenciaEvaluacionC3`]="{ item }">
            <span>{{ formatFecha(item.fechaVigenciaEvaluacionC3) }}</span>
          </template>
          <template v-slot:[`item.acciones`]="{ item }">
            <v-btn icon color="info" small @click="openObservacionesModal(item)">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">mdi-text-box-search-outline</v-icon>
                </template>
                <span>Ver observaciones</span>
              </v-tooltip>
            </v-btn>
            <v-btn
              icon
              color="secondary"
              small
              :loading="loadingEditarResultado === item.idResultado"
              @click="openEditModal(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon color="error" small @click="openDeleteDialog(item)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </template>
    </v-card-text>

    <v-dialog v-model="dialogEliminar" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-h6">Eliminar resultado C3</v-card-title>
        <v-card-text
          >¿Estás seguro de que deseas eliminar este resultado C3? Esta acción no se puede deshacer.</v-card-text
        >
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogEliminar = false">Cancelar</v-btn>
          <v-btn color="error" :loading="loadingEliminarResultado" @click="confirmarEliminacion">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { DIALOG_TYPES } from '../../plugins/dialog-types'
import IconButton from '@/components/base/IconButton.vue'
import { PERFILES } from '../../perfiles'
import { HUMAN_READABLE_DATE_FORMAT } from '@/const'

const PERFILES_ARMERIA = [
  PERFILES.Armeria,
  PERFILES.ARMERIA_LOC,
  PERFILES['ARMERIA_PASE DE LISTA'],
  PERFILES['ARMERIA_PERSONAL_LOC'],
  PERFILES['ARMERIA_CATALOGOS E INVENTARIO ARMAS'],
  PERFILES['ARMERIA_CATALOGOS E INVENTARIO ACCESORIOS'],
]

export default {
  components: {
    IconButton,
  },
  props: {
    personal: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      DIALOG_TYPES,
      loading: false,
      loadingEditarResultado: false,
      dialogEliminar: false,
      loadingEliminarResultado: false,
      resultadoAEliminar: null,
    }
  },

  computed: {
    ...mapGetters('resultadosPersonalesC3', {
      c3Items: 'getResultados',
    }),
    perfilLogeado() {
      return localStorage.getItem('perfil')
    },
    isPerfilArmeria() {
      return PERFILES_ARMERIA.includes(this.perfilLogeado)
    },
    headersC3() {
      const baseHeaders = [
        { text: 'Folio', value: 'numFolio' },
        { text: 'Resultado', value: 'resultado' },
        { text: 'Fecha de emisión C3', value: 'fechaVigenciaEvaluacionC3' },
        { text: 'Opciones', value: 'acciones', sortable: false },
      ]
      if (this.isPerfilArmeria) {
        return baseHeaders.filter((h) => h.value !== 'acciones' && h.value !== 'resultado')
      }
      return baseHeaders
    },
  },

  methods: {
    ...mapActions('resultadosPersonalesC3', ['fetchResultados', 'deleteResultado']),
    ...mapMutations(['SET_SNACKBAR_MESSAGE']),
    ...mapMutations('dialog', ['SET_DIALOG']),

    openObservacionesModal(item) {
      this.SET_DIALOG({
        ...DIALOG_TYPES.OBSERVACIONES_RESULTADO_C3,
        props: { resultadoC3: item },
      })
    },
    async openEditModal(item) {
      this.loadingEditarResultado = item.idResultado
      await this.$nextTick()
      this.loadingEditarResultado = false
      this.SET_DIALOG({
        ...DIALOG_TYPES.EDITAR_RESULTADO_C3,
        props: { resultadoC3: item, idPersonal: this.personal.idPersonal },
      })
    },
    openDeleteDialog(item) {
      this.resultadoAEliminar = item
      this.dialogEliminar = true
    },
    async confirmarEliminacion() {
      this.loadingEliminarResultado = true
      try {
        await this.deleteResultado({
          idPersonal: this.personal.idPersonal,
          idResultado: this.resultadoAEliminar.idResultado,
        })
        this.dialogEliminar = false
        this.resultadoAEliminar = null
      } catch (error) {
        console.error('Error eliminando resultado C3:', error)
        this.SET_SNACKBAR_MESSAGE('Ocurrió un error al eliminar el resultado C3.')
      } finally {
        this.loadingEliminarResultado = false
      }
    },
    formatFecha(fecha) {
      if (!fecha) return ''
      const fechaNormalizada = fecha.replace('a. m.', 'AM').replace('p. m.', 'PM')
      return moment(fechaNormalizada, 'DD/MM/YYYY hh:mm:ss A').format(HUMAN_READABLE_DATE_FORMAT)
    },
  },

  watch: {
    'personal.idPersonal': {
      immediate: true,
      async handler(newVal) {
        if (newVal) {
          this.loading = true
          try {
            await this.fetchResultados(newVal)
          } catch (error) {
            console.error('Error cargando resultados C3:', error)
            this.SET_SNACKBAR_MESSAGE('Ocurrió un error al cargar los resultados C3.')
          } finally {
            this.loading = false
          }
        }
      },
    },
  },
}
</script>
<style scoped>
/* Estilos modernos para AreaAdscripcionComponent - Adaptable a módulos */
.ui-module-card {
  background: transparent !important;
  border-radius: 20px;
  transition: all 0.3s ease;
}

.ui-module-card:hover {
  transform: translateY(-2px);
}

/* Card title */
::v-deep .v-card-title {
  padding: 20px 24px !important;
  color: #e0e0e0 !important;
  position: relative;
  z-index: 1;
}

.text-h5 {
  color: #ffffff !important;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-size: 1.125rem !important;
}

/* Card text */
::v-deep .v-card-text {
  padding: 24px !important;
  color: #e0e0e0 !important;
  position: relative;
  z-index: 1;
}

/* Data table */
::v-deep .v-data-table {
  background: transparent !important;
  color: #e0e0e0 !important;
  position: relative;
  z-index: 1;
}

::v-deep .v-data-table__wrapper {
  border-radius: 12px;
  overflow: hidden;
}

/* Encabezados de tabla - usa el color secondary del tema actual */
::v-deep .v-data-table-header {
  position: relative;
}

::v-deep .v-data-table-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--v-secondary-base);
  opacity: 0.15;
  pointer-events: none;
  z-index: 0;
}

::v-deep .v-data-table-header th {
  color: #e0e0e0 !important;
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 2px solid var(--v-secondary-base) !important;
  padding: 16px !important;
  position: relative;
  z-index: 1;
}

/* Filas de la tabla */
::v-deep .v-data-table tbody tr {
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;
}

::v-deep .v-data-table tbody tr:hover {
  background: rgba(var(--v-secondary-base-rgb), 0.12) !important;
  transform: scale(1.005);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

::v-deep .v-data-table tbody td {
  color: #e0e0e0 !important;
  border-bottom: 1px solid rgba(var(--v-secondary-base-rgb), 0.08) !important;
  padding: 14px 16px !important;
  position: relative;
  z-index: 1;
  font-size: 0.9375rem;
}

/* Data footer */
::v-deep .v-data-footer {
  background: rgba(18, 18, 18, 0.5) !important;
  border-top: 1px solid var(--v-secondary-base) !important;
  color: #e0e0e0 !important;
}

::v-deep .v-data-footer__select .v-select {
  color: #e0e0e0 !important;
}

::v-deep .v-data-footer__pagination {
  color: #e0e0e0 !important;
}

/* Empty state */
::v-deep .v-data-table__empty-wrapper {
  color: rgba(224, 224, 224, 0.6) !important;
  padding: 24px !important;
}

/* Botones */
::v-deep .v-btn {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
  position: relative;
  overflow: hidden;
}

::v-deep .v-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
  pointer-events: none;
  z-index: 0;
}

::v-deep .v-btn:active::before {
  width: 300px;
  height: 300px;
}

::v-deep .v-btn:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

::v-deep .v-btn .v-icon {
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

::v-deep .v-btn:hover .v-icon {
  transform: scale(1.15) rotate(5deg);
}

/* Tooltips */
::v-deep .v-tooltip__content {
  background: rgba(30, 30, 46, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid var(--v-secondary-base) !important;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  color: #e0e0e0 !important;
  font-weight: 500;
  font-size: 0.875rem;
  padding: 8px 12px;
}

/* Scrollbar - usa el color del módulo actual */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(10, 10, 10, 0.5);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: var(--v-secondary-base);
  border-radius: 4px;
  transition: all 0.3s;
}

::-webkit-scrollbar-thumb:hover {
  filter: brightness(1.2);
}
</style>
